import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, Redirect } from 'react-router-dom';
import { Skeleton } from '@material-ui/lab';
import { Box, Button, makeStyles, Typography } from '@popmenu/common-ui';
import { useUserTasteProfileQuery } from '~/libs/gql/queries/eat/userTasteProfileQuery.generated';
import { useEatSession } from '../../hooks/useEatSession';
import { withReturnTo } from '../../hooks/useReturnTo';
import { useSignInPath } from '../../hooks/useSignInPath';
import { formatTasteTrait } from './formatTasteTrait';
import { ProfileDishCard } from './ProfileDishCard';

const REFINE_PATH = withReturnTo('/refine', '/profile');
const DISHES_SHOWN = 8;

const useMessages = () => {
  const intl = useIntl();

  return {
    dishHistory: intl.formatMessage({ defaultMessage: 'Dish history' }),
    learnedFromLikes: (likes: number) => intl.formatMessage(
      { defaultMessage: "What we've learned from {likes, plural, one {# liked dish} other {# liked dishes}}." },
      { likes },
    ),
    learnedFromLikesAndPicks: (likes: number, picks: number) => intl.formatMessage(
      { defaultMessage: "What we've learned from {likes, plural, one {# liked dish} other {# liked dishes}} and {picks, plural, one {# pick} other {# picks}}." },
      { likes, picks },
    ),
    learnedFromNothing: intl.formatMessage({ defaultMessage: "Pick between dishes or like the ones you enjoy, and we'll start learning your taste." }),
    learnedFromPicks: (picks: number) => intl.formatMessage(
      { defaultMessage: "What we've learned from {picks, plural, one {# pick} other {# picks}}." },
      { picks },
    ),
    likedTab: (count: number) => intl.formatMessage({ defaultMessage: 'Liked · {count}' }, { count }),
    picksTab: (count: number) => intl.formatMessage({ defaultMessage: 'Your picks · {count}' }, { count }),
    title: intl.formatMessage({ defaultMessage: 'Your taste profile' }),
    titleWithName: (name: string) => intl.formatMessage({ defaultMessage: "{name}'s taste profile" }, { name }),
  };
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(6),
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(4),
    },
  },
  dishGrid: {
    display: 'grid',
    gap: theme.spacing(2.5),
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(2, 1.5),
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  header: {
    alignItems: 'stretch',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
    justifyContent: 'space-between',
  },
  heading: {
    fontSize: '2.5rem',
    fontWeight: 600,
    lineHeight: 1.15,
    margin: 0,
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.875rem',
    },
  },
  intro: {
    display: 'flex',
    flex: '999 1 480px',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    justifyContent: 'center',
    minWidth: 0,
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  refineButton: {
    borderRadius: 24,
    fontWeight: 600,
    minHeight: 48,
    textTransform: 'none',
  },
  refineCard: {
    // The design system's pm-cream, which the theme doesn't have yet
    backgroundColor: '#F4E9E1',
    borderRadius: theme.spacing(1.5),
    display: 'flex',
    flex: '1 1 320px',
    flexDirection: 'column',
    gap: theme.spacing(1.75),
    padding: theme.spacing(3),
  },
  refineTitle: {
    fontSize: '0.9375rem',
    fontWeight: 600,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
  sectionHeading: {
    fontSize: '1.5rem',
    fontWeight: 600,
    margin: 0,
  },
  showAll: {
    alignSelf: 'flex-start',
    fontWeight: 600,
    marginTop: theme.spacing(2),
    textTransform: 'none',
  },
  stillLearningHeading: {
    fontSize: '1.125rem',
    fontWeight: 600,
    margin: theme.spacing(1, 0, 0),
  },
  tab: {
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: 20,
    fontWeight: 600,
    minHeight: 40,
    textTransform: 'none',
  },
  tabs: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  tabSelected: {
    '&:hover': {
      backgroundColor: theme.palette.text.primary,
    },
    backgroundColor: theme.palette.text.primary,
    borderColor: theme.palette.text.primary,
    color: theme.palette.common.white,
  },
  traitCard: {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(1.5),
    fontSize: '1.1875rem',
    fontWeight: 600,
    lineHeight: 1.25,
    margin: 0,
    padding: theme.spacing(2.5),
  },
  traitCardStillLearning: {
    borderStyle: 'dashed',
    color: theme.palette.text.secondary,
  },
  traitGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
}));

type DishTab = 'liked' | 'picks';

const TasteProfile = () => {
  const classes = useStyles();
  const intl = useIntl();
  const msg = useMessages();
  const { user } = useEatSession();
  const [dishTab, setDishTab] = useState<DishTab | null>(null);
  const [isShowingAllDishes, setIsShowingAllDishes] = useState(false);
  const { data, loading } = useUserTasteProfileQuery({ fetchPolicy: 'network-only', ssr: false });
  const profile = data?.userTasteProfile;

  const name = user?.firstName || user?.displayName;
  const heading = name ? msg.titleWithName(name) : msg.title;

  if (loading || !profile) {
    return (
      <Box aria-busy="true" className={classes.container}>
        <Skeleton height={56} variant="text" width="60%" />
        <Skeleton height={160} variant="rect" />
      </Box>
    );
  }

  // Likes are loved_it and liked_it pops; eat.pop's heart creates loved_it, restaurant sites create both
  const likes = profile.lovesCount;
  const picks = profile.comparisonsCount;
  let summary = msg.learnedFromNothing;
  if (likes && picks) summary = msg.learnedFromLikesAndPicks(likes, picks);
  else if (likes) summary = msg.learnedFromLikes(likes);
  else if (picks) summary = msg.learnedFromPicks(picks);

  const traits = profile.traits.flatMap((trait) => {
    const text = formatTasteTrait(intl, trait);
    return text ? [{ ...trait, text }] : [];
  });
  const confidentTraits = traits.filter(trait => trait.isConfident);
  const stillLearningTraits = traits.filter(trait => !trait.isConfident);

  // Opens on whichever list has dishes, liked first
  const selectedTab: DishTab = dishTab ?? (profile.lovedMenuItemMatches.length || !profile.pickedMenuItemMatches.length ? 'liked' : 'picks');
  const dishMatches = selectedTab === 'liked' ? profile.lovedMenuItemMatches : profile.pickedMenuItemMatches;
  const tabs: { label: string, value: DishTab }[] = [
    { label: msg.likedTab(profile.lovedMenuItemMatches.length), value: 'liked' },
    { label: msg.picksTab(profile.pickedMenuItemMatches.length), value: 'picks' },
  ];

  return (
    <Box className={classes.container}>
      <Box className={classes.header} component="header">
        <Box className={classes.intro}>
          <Typography className={classes.heading} component="h1">{heading}</Typography>
          <Typography className={classes.muted}>{summary}</Typography>
        </Box>
        <Box aria-labelledby="refine-title" className={classes.refineCard} component="section">
          <Typography className={classes.refineTitle} component="h2" id="refine-title">
            <FormattedMessage defaultMessage="Refine your palate" />
          </Typography>
          <Typography variant="body2">
            <FormattedMessage
              defaultMessage="Each round of 15 quick picks teaches us more about what you like."
            />
          </Typography>
          <Button className={classes.refineButton} color="primary" component={Link} role="link" to={REFINE_PATH} variant="contained">
            <FormattedMessage defaultMessage="Play a round" />
          </Button>
        </Box>
      </Box>

      <Box aria-labelledby="traits-title" className={classes.section} component="section">
        <Typography className={classes.sectionHeading} component="h2" id="traits-title">
          <FormattedMessage defaultMessage="What we think you like" />
        </Typography>
        {traits.length === 0 && (
          <Typography className={classes.muted}>
            <FormattedMessage
              defaultMessage="Nothing stands out yet. A round of picks is the quickest way to change that."
            />
          </Typography>
        )}
        {confidentTraits.length > 0 && (
          <ul className={classes.traitGrid}>
            {confidentTraits.map(trait => (
              <li key={trait.feature} className={classes.traitCard}>{trait.text}</li>
            ))}
          </ul>
        )}
        {stillLearningTraits.length > 0 && (
          <React.Fragment>
            <Typography className={classes.stillLearningHeading} component="h3">
              <FormattedMessage defaultMessage="Still learning" />
            </Typography>
            <ul className={classes.traitGrid}>
              {stillLearningTraits.map(trait => (
                <li key={trait.feature} className={`${classes.traitCard} ${classes.traitCardStillLearning}`}>{trait.text}</li>
              ))}
            </ul>
          </React.Fragment>
        )}
      </Box>

      <Box aria-labelledby="dishes-title" className={classes.section} component="section">
        <Typography className={classes.sectionHeading} component="h2" id="dishes-title">
          <FormattedMessage defaultMessage="Your dishes" />
        </Typography>
        <Box aria-label={msg.dishHistory} className={classes.tabs} role="tablist">
          {tabs.map(tab => (
            <Button
              key={tab.value}
              aria-controls="profile-dishes"
              aria-selected={selectedTab === tab.value}
              className={`${classes.tab} ${selectedTab === tab.value ? classes.tabSelected : ''}`}
              id={`profile-dishes-tab-${tab.value}`}
              onClick={() => {
                setDishTab(tab.value);
                setIsShowingAllDishes(false);
              }}
              role="tab"
            >
              {tab.label}
            </Button>
          ))}
        </Box>
        <Box aria-labelledby={`profile-dishes-tab-${selectedTab}`} id="profile-dishes" role="tabpanel">
          {dishMatches.length === 0 ? (
            <Typography className={classes.muted}>
              {selectedTab === 'liked' ?
                <FormattedMessage defaultMessage="Tap the heart on a dish you like to save it." /> :
                <FormattedMessage defaultMessage="Dishes you pick in Refine your palate show up here." />}
            </Typography>
          ) : (
            <React.Fragment>
              <Box className={classes.dishGrid}>
                {(isShowingAllDishes ? dishMatches : dishMatches.slice(0, DISHES_SHOWN)).map(match => (
                  <ProfileDishCard key={match.menuItem.id} dish={match.menuItem} traits={match.traits} />
                ))}
              </Box>
              {!isShowingAllDishes && dishMatches.length > DISHES_SHOWN && (
                <Button className={classes.showAll} onClick={() => setIsShowingAllDishes(true)} variant="text">
                  <FormattedMessage defaultMessage="Show all {count} dishes" values={{ count: dishMatches.length }} />
                </Button>
              )}
            </React.Fragment>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// What we've learned about the signed-in diner's taste, in words. Read-only for now: no corrections or settings.
export const ProfilePage = () => {
  const msg = useMessages();
  const signInPath = useSignInPath();
  const { loading, user } = useEatSession();

  if (!loading && !user) return <Redirect to={signInPath} />;

  return (
    <React.Fragment>
      <Helmet title={msg.title}>
        <meta name="robots" content="noindex" />
      </Helmet>
      {user && <TasteProfile />}
    </React.Fragment>
  );
};
